import React from 'react';
import ReactApexChartOriginal from 'react-apexcharts';
import { i18n } from "../../translate/i18n";
import { Typography } from '@material-ui/core';
import { sanitizeApexOptions } from './sanitizeApexOptions';

class ApexErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Evita quebrar o dashboard inteiro por erro interno do ApexCharts
    console.error("[SafeReactApexChart] ApexCharts runtime error:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          width: this.props.width || '100%',
          height: this.props.height || '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <Typography style={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.9rem', textAlign: 'center' }}>
            {i18n.t("dashboard.noData") || "Erro ao carregar gráfico"}
          </Typography>
        </div>
      );
    }

    return this.props.children;
  }
}

// ✅ WRAPPER GLOBAL: Interceptar TODAS as chamadas ao ReactApexChart
// Este wrapper garante que NUNCA um undefined chegue ao ApexCharts
// Use este componente em vez de importar ReactApexChart diretamente
const SafeReactApexChart = React.memo((props) => {
  const { options = {}, series = [], type = 'bar', ...restProps } = props;
  
  // ✅ SANITIZAÇÃO ULTRA-AGGRESSIVA: Usar sanitizeApexOptions + Proxy para interceptar acessos
  const ultraSafeOptions = React.useMemo(() => {
    try {
      if (!options || typeof options !== 'object') return {};
      
      // Primeiro, usar a sanitização do OptimizedCharts
      let sanitized = sanitizeApexOptions(options);
      
      // Depois, fazer uma limpeza final ultra-agressiva para garantir
      const deepClean = (obj, depth = 0) => {
        if (depth > 30) return {};
        if (!obj || typeof obj !== 'object') {
          if (typeof obj === 'string') return obj.trim() || '';
          return obj;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(item => {
            if (item == null || item === undefined) return '';
            if (typeof item === 'string') {
              const trimmed = item.trim();
              return trimmed || '';
            }
            if (typeof item === 'object') return deepClean(item, depth + 1);
            return item;
          }).filter(item => item !== null && item !== undefined && item !== '');
        }
        
        const result = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            const val = obj[key];
            
            // Pular funções
            if (typeof val === 'function') {
              result[key] = val;
              continue;
            }
            
            // Substituir undefined/null por valores padrão baseados na chave
            if (val === undefined || val === null) {
              if (key.includes('color') || key.includes('Color')) {
                result[key] = '#000000';
              } else if (key.includes('font') || key.includes('Font')) {
                result[key] = 'Arial, sans-serif';
              } else if (key === 'theme') {
                result[key] = 'light';
              } else if (key === 'type') {
                result[key] = 'bar';
              }
              // Não adicionar outros campos undefined
              continue;
            }
            
            // Se for string, garantir que não seja vazia
            if (typeof val === 'string') {
              const trimmed = val.trim();
              result[key] = trimmed || (key.includes('color') ? '#000000' : '');
            }
            // Se for objeto, limpar recursivamente
            else if (typeof val === 'object') {
              result[key] = deepClean(val, depth + 1);
            }
            // Outros tipos, manter como está
            else {
              result[key] = val;
            }
          }
        }
        return result;
      };
      
      const cleaned = deepClean(sanitized);
      
      // ✅ CRÍTICO: Criar um Proxy que intercepta TODOS os acessos a propriedades
      // Isso garante que mesmo se o ApexCharts acessar propriedades aninhadas, elas nunca serão undefined
      const createSafeProxy = (target, path = '') => {
        return new Proxy(target, {
          get: (obj, prop) => {
            const value = obj[prop];
            
            // Se a propriedade não existe ou é undefined, retornar valor padrão
            if (value === undefined || value === null) {
              const propStr = String(prop);
              if (propStr.includes('color') || propStr.includes('Color')) {
                return '#000000';
              } else if (propStr.includes('font') || propStr.includes('Font')) {
                return 'Arial, sans-serif';
              } else if (propStr === 'theme') {
                return 'light';
              } else if (propStr === 'type') {
                return 'bar';
              } else if (typeof prop === 'string' && prop.length > 0) {
                // Para outras propriedades string, retornar string vazia
                return '';
              }
              return undefined;
            }
            
            // Se for string, garantir que seja válida
            if (typeof value === 'string') {
              const trimmed = value.trim();
              return trimmed || (String(prop).includes('color') ? '#000000' : '');
            }
            
            // Se for objeto ou array, criar proxy recursivo
            if (typeof value === 'object' && value !== null) {
              return createSafeProxy(value, `${path}.${String(prop)}`);
            }
            
            return value;
          }
        });
      };
      
      return createSafeProxy(cleaned);
    } catch (e) {
      console.error('Erro ao sanitizar options no wrapper global:', e, options);
      return {};
    }
  }, [options]);
  
  const ultraSafeSeries = React.useMemo(() => {
    try {
      if (!series) return [];
      
      // ✅ CORREÇÃO: Para gráficos radialBar, series é um array de números, não objetos
      if (type === 'radialBar' || type === 'radial') {
        if (!Array.isArray(series)) return [0];
        return series.map(v => {
          if (v == null || v === undefined || isNaN(v)) return 0;
          const num = typeof v === 'number' ? v : parseFloat(v);
          return isNaN(num) ? 0 : Math.max(0, Math.min(100, num)); // Garantir entre 0-100
        });
      }
      
      // Para outros tipos de gráfico, tratar como objetos
      if (!Array.isArray(series)) return [];
      return series.map(s => {
        if (!s || typeof s !== 'object') {
          // Se for número, tratar como data point
          if (typeof s === 'number' && !isNaN(s)) {
            return { name: '', data: [s] };
          }
          return { name: '', data: [] };
        }
        return {
          ...s,
          name: (s.name != null && typeof s.name === 'string') ? s.name.trim() : '',
          data: Array.isArray(s.data) 
            ? s.data.map(v => {
                if (v == null || v === undefined || isNaN(v)) return 0;
                return typeof v === 'number' ? v : parseFloat(v) || 0;
              })
            : []
        };
      });
    } catch (e) {
      console.error('Erro ao sanitizar series no wrapper global:', e);
      return type === 'radialBar' || type === 'radial' ? [0] : [];
    }
  }, [series, type]);
  
  const ultraSafeType = React.useMemo(() => {
    if (type && typeof type === 'string' && type.trim()) {
      return type.trim();
    }
    return 'bar';
  }, [type]);
  
  // ✅ Try-catch final para capturar QUALQUER erro
  try {
    return (
      <ApexErrorBoundary width={restProps.width} height={restProps.height}>
        <ReactApexChartOriginal
          {...restProps}
          options={ultraSafeOptions}
          series={ultraSafeSeries}
          type={ultraSafeType}
        />
      </ApexErrorBoundary>
    );
  } catch (error) {
    console.error('Erro crítico no ReactApexChart wrapper:', error);
    return (
      <div style={{ 
        width: restProps.width || '100%', 
        height: restProps.height || '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '20px'
      }}>
        <Typography style={{ color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.9rem', textAlign: 'center' }}>
          {i18n.t("dashboard.noData") || "Erro ao carregar gráfico"}
        </Typography>
      </div>
    );
  }
});

SafeReactApexChart.displayName = 'SafeReactApexChart';

// Exportar como default para compatibilidade
export default SafeReactApexChart;

// Também exportar como named export
export { SafeReactApexChart };
